import { CloseCircleOutlined, FilterOutlined } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Skeleton,
  Space,
  Typography,
} from 'antd';

import { CustomAlert, MultipleSelect } from '@app/components';
import {
  DATE_PICKER_FORMAT,
  ERROR,
  OFF,
  PREFIX,
  rangePickerLocale,
  today,
  fistDayOfCurrentMonth,
} from '@app/constants';
import { useContentWidth } from '@app/hooks';

import { useAnalytics } from '@pages/Analytics/AnalyticsService/hooks/useAnalytics';

import { Widget } from '@pages/Analytics/AnalyticsService/components/Widget.main';

import type { FC } from 'react';

import '@pages/Analytics/AnalyticsService/styles/Analytics.desktop.scss';
import '@pages/Analytics/AnalyticsService/styles/Analytics.tablet.scss';
import '@pages/Analytics/AnalyticsService/styles/Analytics.mobile.scss';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;

export const AnalyticsService: FC = () => {
  const {
    isFiltersLoading,
    form,
    searchError,
    setSearchError,
    selectedEmployees,
    setSelectedEmployees,
    selectedUsers,
    setSelectedUsers,
    selectedClientServices,
    setSelectedClientServices,
    selectedClientServiceCategories,
    setSelectedClientServiceCategories,
    widgetConfig,
    handleReset,
    submitForm,
    employeesSelectOptions,
    clientServiceSelectOptions,
    clientServiceCategoriesSelectOptions,
    userSelectOptions,
  } = useAnalytics();
  const { contentWidth } = useContentWidth();

  if (isFiltersLoading) {
    return <Skeleton active />;
  }

  return (
    <div className={`${PREFIX}analytics`}>
      <div className="header-container">
        <Form
          form={form}
          autoComplete={OFF}
          onFinish={(values) => submitForm(values)}
          labelCol={{ span: 4 }}
          wrapperCol={{ span: 30 }}
          style={{ marginBottom: 10 }}
          layout="vertical"
          initialValues={{
            Date: [fistDayOfCurrentMonth, today],
          }}
        >
          <div className="date-container">
            <div
              className="date-item"
              style={{ flexDirection: contentWidth > 397 ? 'row' : 'column' }}
            >
              <Text className="label">Период:</Text>
              <Form.Item name="Date">
                <RangePicker
                  className="dates-range"
                  format={DATE_PICKER_FORMAT}
                  size="middle"
                  allowEmpty={[true, true]}
                  locale={rangePickerLocale}
                  style={{ width: contentWidth > 397 ? '311px' : '100%' }}
                />
              </Form.Item>
            </div>
          </div>

          <div className="selectors-container">
            <div className="selectors">
              <MultipleSelect
                className="user-selector"
                placeholder="Все исполнители"
                allOptions={employeesSelectOptions}
                width={contentWidth > 397 ? 100 : '100%'}
                selectedOption={selectedEmployees}
                setSelectedOptions={setSelectedEmployees}
                filterName="filterName"
              />

              <MultipleSelect
                className="user-selector"
                placeholder="Все пользователи"
                allOptions={userSelectOptions}
                width={contentWidth > 397 ? 100 : '100%'}
                selectedOption={selectedUsers}
                setSelectedOptions={setSelectedUsers}
                filterName="filterName"
              />

              <MultipleSelect
                className="user-selector"
                placeholder="Все категории услуг"
                allOptions={clientServiceCategoriesSelectOptions}
                width={contentWidth > 397 ? 200 : '100%'}
                selectedOption={selectedClientServiceCategories}
                setSelectedOptions={setSelectedClientServiceCategories}
                filterName="filterName"
              />

              <MultipleSelect
                className="user-selector"
                placeholder="Все услуги"
                allOptions={clientServiceSelectOptions}
                width={contentWidth > 397 ? 200 : '100%'}
                selectedOption={selectedClientServices}
                setSelectedOptions={setSelectedClientServices}
                filterName="filterName"
              />

              <Form.Item
                style={{
                  display: 'inline-block',
                  marginBottom: 0,
                }}
                className="buttons-container"
              >
                {searchError && (
                  <CustomAlert
                    errorType={ERROR}
                    serverError={searchError}
                    setServerError={setSearchError}
                  />
                )}
                <Space className="tablet-buttons" size="middle">
                  <Button
                    htmlType="reset"
                    onClick={handleReset}
                    className="button"
                  >
                    <CloseCircleOutlined />
                    Сброс
                  </Button>

                  <Button type="primary" htmlType="submit" className="button">
                    <FilterOutlined />
                    Показать
                  </Button>
                </Space>
              </Form.Item>
            </div>

            <Form.Item
              style={{
                display: 'inline-block',
                marginBottom: 0,
              }}
              className="buttons-container"
            >
              {searchError && (
                <CustomAlert
                  errorType={ERROR}
                  serverError={searchError}
                  setServerError={setSearchError}
                />
              )}
              <Space className="desktop-buttons" size="middle">
                <Button
                  htmlType="reset"
                  onClick={handleReset}
                  className="button"
                >
                  <CloseCircleOutlined />
                  Сброс
                </Button>

                <Button type="primary" htmlType="submit" className="button">
                  <FilterOutlined />
                  Показать
                </Button>
              </Space>
            </Form.Item>
          </div>
        </Form>
      </div>

      <Flex vertical>
        {widgetConfig.map(({ name, widgets, className }) => (
          <div className="widget-group" key={name}>
            <Title level={5} className="widget-group-title">
              {name}
            </Title>
            <div className={`widgets-container ${className}`}>
              {widgets.map((widget) => (
                <Widget
                  key={widget.widgetId}
                  dataWidgetId={widget.widgetId}
                  isLoading={widget.isLoading}
                  data={widget.data}
                  infoText={widget.infoText}
                  title={widget.title}
                  mainValueSign={widget.mainValueSign}
                />
              ))}
            </div>
          </div>
        ))}
      </Flex>
    </div>
  );
};
